/* eslint-disable */
import { create } from 'zustand';

// ============================================================
// shellStore — Shell v3 (CLB-012 / S1) chrome state.
// ============================================================
// Separate from uiStore (which sits at the 300-line cap). Holds ONLY what the
// v3 chrome needs across components: how tall the fixed chrome currently is
// (App.js writes it into --app-header-height so every portal's content
// offset follows), whether the app bar is condensed, which popover/panel is
// open (one at a time — the prototype's closeMenus/togglePanel contract), the
// per-user density and the saved-view registry version counter.
//
// localStorage keys (per-browser conveniences, never state the server needs):
//   clb.shell.density  'cards' | 'compact'
// ============================================================

const DENSITY_KEY = 'clb.shell.density';

function readDensity() {
  try { return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'cards'; }
  catch (e) { return 'cards'; }
}

export const useShellStore = create((set, get) => ({
  // true while ShellRoot is mounted — the portals read it to swap their v3
  // surfaces (the dev card, S2) without a prop thread through App.js.
  active: false,
  setActive: (v) => set((s) => (s.active === !!v ? s : { active: !!v })),

  // Chrome geometry — the app bar (56) + the filter bar (48, when the portal has
  // one) + a third row (40, Orders Table rail / Collection shape rail).
  appBarH: 56,
  filterBarH: 0,
  railH: 0,
  setChrome: (patch) => set((s) => ({ ...s, ...patch })),
  chromeHeight: () => { const s = get(); return s.appBarH + s.filterBarH + s.railH; },

  // Condense on scroll — the app bar slides away, the filter bar sticks at 0.
  condensed: false,
  setCondensed: (v) => set((s) => (s.condensed === v ? s : { condensed: v })),

  // One open popover at a time: 'notif' | 'dead' | 'filter:<key>' | 'menu:<id>' | null
  open: null,
  setOpen: (key) => set((s) => (s.open === key ? s : { open: key })),
  toggleOpen: (key) => set((s) => ({ open: s.open === key ? null : key })),
  closeAll: () => set((s) => (s.open === null ? s : { open: null })),

  // Density — the cards grid reads it (S2/S3).
  density: readDensity(),
  setDensity: (d) => {
    const v = d === 'compact' ? 'compact' : 'cards';
    try { localStorage.setItem(DENSITY_KEY, v); } catch (e) { /* no storage — session only */ }
    set({ density: v });
  },

  // Section counts published by a portal's own (hidden under v3) nav — the
  // identity's section menu shows them: { [scope]: { [sectionKey]: n } } (S5).
  sectionCounts: {},
  setSectionCounts: (scope, map) => set((s) => {
    const prev = s.sectionCounts[scope] || {}; const keys = Object.keys(map || {});
    if (keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === map[k])) return s;
    return { sectionCounts: { ...s.sectionCounts, [scope]: { ...(map || {}) } } };
  }),

  // A navigation guard a screen with unsaved edits publishes (the Orders Table's
  // guardedRun): the bar's world / section switches run through it (S5).
  navGuard: null,
  setNavGuard: (fn) => set((s) => (s.navGuard === fn ? s : { navGuard: fn || null })),

  // Published bars (S6) — a portal whose filter state lives in ITS OWN
  // component tree (QC's lifted home view, the Sales period, the Projects
  // selection) publishes the bar config it wants rendered; the shell merges it
  // over the identity row (GenericBar) and renders. The portal stays the
  // source of truth; the shell is a renderer. Keyed by portal key or tab.
  bars: {},
  publishBar: (key, cfg) => set((s) => ({ bars: { ...s.bars, [key]: cfg } })),
  unpublishBar: (key) => set((s) => { if (!(key in s.bars)) return s; const next = { ...s.bars }; delete next[key]; return { bars: next }; }),

  // Based-on peek (S2) — the one fixed layer, anchored to a chip.
  peek: null,          // { dev, anchorRect } | null
  peekPinned: false,
  setPeek: (peek, pinned = false) => set({ peek, peekPinned: pinned }),
  clearPeek: (force = false) => set((s) => ((s.peekPinned && !force) ? s : { peek: null, peekPinned: false })),
}));
